import { useState } from 'react';
import { toast } from 'sonner';
import { History, Pencil, Plus, Trash2, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import { useRateHistory } from '@/hooks/payroll/useRateHistory';
import { useDeleteRatePeriod } from '@/hooks/payroll/useRatePeriodMutations';
import { usePermission } from '@/hooks/usePermission';
import { RateApiError, type RatePeriod } from '@/services/rateApi.service';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { SectionHeader } from '@/components/ui/section-header';
import { RatePeriodModal } from '@/components/payroll/RatePeriodModal';

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** `2026-08` → `Aug 2026`. */
function formatMonth(key: string): string {
  const year = Number(key.slice(0, 4));
  const month = Number(key.slice(5, 7));
  return `${MONTH_LABELS[month - 1] ?? month} ${year}`;
}

/**
 * A period starting in 1900 is the baseline a first-ever rate was back-dated to —
 * it means "from the beginning", not January 1900.
 */
function formatRange(period: RatePeriod): string {
  const isBaseline = period.effective_from.startsWith('1900');
  const from = isBaseline ? 'Initial' : formatMonth(period.effective_from);

  if (!period.effective_to) return isBaseline ? 'Initial rate' : `${from} onwards`;
  if (period.effective_to === period.effective_from) return from;
  return `${from} – ${formatMonth(period.effective_to)}`;
}

function errorMessage(e: unknown): string {
  return e instanceof RateApiError ? e.message : 'Something went wrong';
}

function Trend({ previous, rate }: { previous: number | null; rate: number }) {
  if (previous === null) return <Badge variant="secondary" className="text-[10px]">Initial</Badge>;
  if (rate > previous) {
    return (
      <span className="flex items-center gap-1 text-xs font-medium text-emerald-600">
        <TrendingUp className="h-3 w-3" />+₹{(rate - previous).toFixed(2)}
      </span>
    );
  }
  if (rate < previous) {
    return (
      <span className="flex items-center gap-1 text-xs font-medium text-rose-500">
        <TrendingDown className="h-3 w-3" />−₹{(previous - rate).toFixed(2)}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-xs text-muted-foreground">
      <Minus className="h-3 w-3" />No change
    </span>
  );
}

interface Props {
  userId: string;
  employeeName: string;
}

/**
 * An employee's rate periods, with the controls to add, edit and remove them.
 *
 * Financial Summary shows only the rate in force right now, which hides that
 * payroll prices each month at the rate that applied *then*. This lists the
 * periods behind that number, so a month's salary can be checked against the rate
 * it was actually computed with.
 */
export function RateHistorySection({ userId, employeeName }: Props) {
  const { has } = usePermission();
  const canManage = has(PERMISSIONS.SALARY_MANAGE);

  const { data: periods = [], isLoading, isError, error, refetch } = useRateHistory(userId);
  const remove = useDeleteRatePeriod(userId);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<RatePeriod | null>(null);
  const [deleting, setDeleting] = useState<RatePeriod | null>(null);

  // Oldest first for the trend comparison, newest first for reading.
  const rows = periods
    .map((period, i) => ({
      period,
      previous: i > 0 ? Number(periods[i - 1]!.rate) : null,
    }))
    .reverse();

  // The period payroll uses this month — not necessarily the newest, since a rate
  // carries forward past the end of its period until the next one starts.
  const currentKey = new Date().toISOString().slice(0, 7);
  const activeId = periods.filter((p) => p.effective_from <= currentKey).pop()?.rate_id;

  const openAdd = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (period: RatePeriod) => { setEditing(period); setModalOpen(true); };

  const confirmDelete = () => {
    if (!deleting) return;
    remove.mutate(deleting.rate_id, {
      onSuccess: () => toast.success('Rate period removed'),
      onError: (e) => toast.error(errorMessage(e)),
    });
  };

  return (
    <Card>
      <SectionHeader
        icon={<History />}
        title="Rate History"
        action={
          canManage ? (
            <Button size="sm" onClick={openAdd}>
              <Plus className="mr-1 h-4 w-4" />Add Period
            </Button>
          ) : undefined
        }
      />
      <CardContent className="p-4">
        {isLoading ? (
          <div className="space-y-2">
            {[0, 1].map((i) => <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />)}
          </div>
        ) : isError ? (
          <div className="py-6 text-center">
            <p className="text-sm text-muted-foreground">{errorMessage(error)}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {canManage
              ? 'No rate set for this employee yet — add the first period above.'
              : 'No rate set for this employee yet.'}
          </p>
        ) : (
          <div className="divide-y divide-border">
            {rows.map(({ period, previous }) => (
              <div key={period.rate_id} className="flex items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{formatRange(period)}</p>
                  {period.rate_id === activeId && (
                    <span className="text-[10px] uppercase tracking-wider text-emerald-600">
                      Currently applied
                    </span>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                  <Trend previous={previous} rate={Number(period.rate)} />
                  <span className="w-20 text-right text-sm font-semibold">
                    ₹{Number(period.rate).toFixed(2)}
                  </span>
                  {canManage && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        aria-label="Edit period"
                        onClick={() => openEdit(period)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        aria-label="Delete period"
                        onClick={() => setDeleting(period)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {rows.length > 0 && (
          <p className="mt-3 text-xs text-muted-foreground">
            Each month is priced at the rate in force that month. A month without its own
            period inherits the most recent earlier one.
          </p>
        )}
      </CardContent>

      <RatePeriodModal
        userId={userId}
        employeeName={employeeName}
        period={editing}
        periods={periods}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(next) => !next && setDeleting(null)}
        title="Remove rate period?"
        description={
          deleting
            ? `${formatRange(deleting)} at ₹${Number(deleting.rate).toFixed(2)}/hr will be removed. Months it covered will fall back to the previous rate.`
            : ''
        }
        confirmLabel="Remove"
        onConfirm={confirmDelete}
      />
    </Card>
  );
}
